import React from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { APP_STORE_URL, GOOGLE_PLAY_URL, MICROSOFT_STORE_URL, openStoreUrl } from '@/components/PublicFooter/publicFooterStoreLinks';
import { useInteractive, useLanding } from './LandingKit';
import useResponsiveness from '@/hooks/useResponsiveness';
import LandingHeroComposer from './LandingHeroComposer';
import HeroBackground from './HeroBackground';
import LandingScrollCue from './LandingScrollCue';
import PageVideo from '@/components/Tutorials/PageVideo';
import { neutralColors } from '@/constants/neutralColors';
import ReferralAppHandoff from './ReferralAppHandoff';
import { content } from '@/lib/landingPageSeo';
import { LAYOUT, fluid, isWeb } from './landingTokens';

const PLATFORMS = [
  { label: 'Mac', short: 'Mac', icon: 'laptop', href: '/mac' },
  { label: 'Windows', short: 'Windows', icon: 'desktop', url: MICROSOFT_STORE_URL },
  { label: 'iPhone & iPad', short: 'iPhone', icon: 'mobile-screen-button', url: APP_STORE_URL, mobile: true },
  { label: 'Android', short: 'Android', icon: 'mobile-screen-button', url: GOOGLE_PLAY_URL, mobile: true },
  { label: 'Chrome & Brave', short: 'Chrome', icon: 'globe', href: '/extension' },
];

// Marketing pages never navigate away on their own; the end card waits for a click.
const HERO_VIDEO_NEXT = { label: 'Learn to use AI with free lessons', href: '/docs#learn', autoplay: false, action: 'Learn about AI' };
const HERO_VIDEO_SIGNUP = { label: 'Create a free account', href: '/signup', title: 'Put AI to work for you.' };
const HERO_USE_CASES = [
  { label: 'Small businesses', href: '/use-cases/small-business' },
  { label: 'Writers', href: '/use-cases/writers' },
  { label: 'Developers', href: '/use-cases/developers' },
  { label: 'Creators', href: '/use-cases/content-creators' },
  { label: 'Marketers', href: '/use-cases/marketers' },
  { label: 'Teachers', href: '/use-cases/teachers' },
  { label: 'Students', href: '/use-cases/students' },
  { label: 'Homeschoolers', href: '/use-cases/homeschoolers' },
  { label: 'Churches & ministries', href: '/use-cases/churches' },
  { label: 'Veterans', href: '/use-cases/veterans' },
  { label: 'Everyone', href: '/use-cases' },
];

function HeroUseCases() {
  const { c, isXS } = useLanding();
  return (
    <View accessibilityLabel="Find your use case" style={{ alignItems: 'center', marginTop: 8, width: '100%', maxWidth: 620 }}>
      <Text style={{ color: c.muted, fontSize: 12 }}>Made for</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', columnGap: isXS ? 12 : 16, rowGap: 0, marginTop: 2 }}>
      {HERO_USE_CASES.map(item => (
        <Link key={item.href} href={item.href} asChild>
          <Pressable accessibilityRole="link" style={{ minHeight: 36, justifyContent: 'center' }}>
            <Text weight="medium" style={{ color: c.muted, fontSize: 12, textDecorationLine: 'underline' }}>{item.label}</Text>
          </Pressable>
        </Link>
      ))}
      </View>
    </View>
  );
}

function PlatformLink({ platform }) {
  const { isDark, isXS } = useLanding();
  const colors = neutralColors({}, isDark);
  const { hovered, handlers } = useInteractive();
  const color = hovered ? colors.text_color_primary : colors.text_color_secondary;
  const link = (
    <Pressable
      {...handlers}
      {...(platform.url ? (isWeb
        ? { href: platform.url, hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' } }
        : { onPress: () => openStoreUrl(platform.url) }) : {})}
      accessibilityRole="link"
      accessibilityLabel={`Gab AI for ${platform.label}`}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 32, ...(isWeb ? { cursor: 'pointer' } : {}) }}
    >
      <Icon name={platform.icon} size={12} color={color} />
      <Text weight="medium" style={{ color, fontSize: 13, ...(isWeb ? { textDecorationLine: 'underline' } : {}) }}>{isXS ? platform.short : platform.label}</Text>
    </Pressable>
  );
  return platform.href ? <Link href={platform.href} asChild>{link}</Link> : link;
}

function HeroPlatforms() {
  const { isDark, isXS } = useLanding();
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', columnGap: isXS ? 13 : 18, rowGap: 2 }}>
      <Text weight="medium" style={{ color: isDark ? '#FFFFFF' : '#040404', fontSize: 13 }}>Download our apps</Text>
      {PLATFORMS.filter(platform => !isXS || platform.mobile).map(platform => <PlatformLink key={platform.label} platform={platform} />)}
    </View>
  );
}

function LandingEditorialHero({ topInset = 0 }) {
  const { isDark, width, isXS, pad, display } = useLanding();
  const { windowHeight } = useResponsiveness();
  const colors = neutralColors({}, isDark);
  const headline = display({ min: 46, max: 104, leading: 1.04, tracking: -0.055 });
  /* Fill the first screen on mobile and center the hero below the 64px
   * public header. Dynamic viewport units account for mobile browser chrome;
   * minHeight lets the content grow and scroll on shorter screens. */
  const centerHero = isWeb && (isXS || windowHeight >= 560);
  const mobileHeaderInset = isWeb && isXS ? 64 : 0;

  return (
    <View accessibilityLabel="Gab AI" {...(isWeb ? { dataSet: { landingHero: 'true' } } : {})} style={{ width: '100%', alignItems: 'center', backgroundColor: isDark ? '#040404' : '#FFFFFF', position: 'relative', overflow: 'hidden' }}>
      <HeroBackground isDark={isDark} />
      <View style={{ width: '100%', maxWidth: LAYOUT.max, paddingHorizontal: pad, paddingTop: topInset + mobileHeaderInset + (centerHero ? (isXS ? 12 : 88) : (isWeb ? fluid(width, 88, 112) : fluid(width, 112, 160))), paddingBottom: centerHero ? 60 : fluid(width, 80, 112), minHeight: centerHero ? '100dvh' : undefined, justifyContent: centerHero ? 'center' : undefined, alignItems: 'center' }}>
        <Text tag="h1" weight="extraBold" style={{ color: colors.text_color_primary, ...headline, maxWidth: '100%', textAlign: 'center', ...(isWeb ? { marginBottom: 8 } : {}) }}>
          {content.headline}
        </Text>

        {isWeb && (
          <View style={{ width: '100%', maxWidth: 640 }}>
            <PageVideo page="home" next={HERO_VIDEO_NEXT} signup={HERO_VIDEO_SIGNUP} playButton className="page-video page-video-hero" />
          </View>
        )}
        <LandingHeroComposer style={{ marginTop: isWeb ? fluid(width, 24, 32) : fluid(width, 36, 48) }} />

        <View style={{ alignItems: 'center', gap: isXS ? 8 : 10, marginTop: fluid(width, 22, 28), paddingVertical: isXS ? 10 : 8, paddingHorizontal: isXS ? 14 : 22, width: '100%', maxWidth: 680 }}>
          <HeroPlatforms />
          <ReferralAppHandoff />
          <HeroUseCases />
        </View>
      </View>
      <LandingScrollCue />
    </View>
  );
}

export default React.memo(LandingEditorialHero);
